import { useState } from 'react';
import { Form } from 'antd';
import { SalePropertyCardController, SalePropertyCardFragmentProps } from './interfaces';
import { PropertyPhotoDto, SaleListingDto, SalePropertyDto } from 'services/sales/sale.dto';
import { useMessenger } from 'tools/view-hooks/messenger-hook';

const STATUS_MAP: Record<string, { color: string; label: string }> = {
    active: { color: 'green', label: 'Activo' },
    paused: { color: 'orange', label: 'Pausado' },
    closed: { color: 'red', label: 'Cerrado' },
    error: { color: 'red', label: 'Error' },
};

export const useSalePropertyCardController = (props: SalePropertyCardFragmentProps): SalePropertyCardController => {
    const { saleProperty, salesService, onRemoved, onUpdated, onListingUpdated } = props;
    const messenger = useMessenger();

    const [editForm] = Form.useForm();
    const [sp, setSp] = useState<SalePropertyDto>(saleProperty);
    const [photos, setPhotos] = useState<PropertyPhotoDto[]>(saleProperty.property?.photos ?? []);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
    const [isEditLoading, setIsEditLoading] = useState(false);
    const [isPublishModalVisible, setIsPublishModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);

    const activeListing = sp.listings?.find((l) => l.status === 'active' || l.status === 'paused');
    const statusInfo = activeListing ? STATUS_MAP[activeListing.status] ?? STATUS_MAP['error'] : undefined;

    const prop = sp.property;
    const propertyAddress = [prop?.street, prop?.number, prop?.floorDepartment, prop?.district, prop?.city]
        .filter(Boolean)
        .join(' ');

    const onToggleExpand = () => setIsExpanded((v) => !v);

    const onPublishPressed = () => setIsPublishModalVisible(true);
    const onPublishCanceled = () => setIsPublishModalVisible(false);
    const onPublished = (listing: SaleListingDto) => {
        setIsPublishModalVisible(false);
        const updated = { ...sp, listings: [...(sp.listings ?? []), listing] };
        setSp(updated);
        onListingUpdated(updated);
    };

    const onEditPressed = () => {
        editForm.setFieldsValue({ price: sp.price, currency: sp.currency, description: sp.description });
        setIsEditModalVisible(true);
    };
    const onEditCanceled = () => setIsEditModalVisible(false);
    const handleUpdated = (updated: SalePropertyDto) => {
        setIsEditModalVisible(false);
        setSp(updated);
        onUpdated(updated);
    };
    const onEditSubmit = (values: { price: number; currency: 'USD' | 'ARS'; description?: string }) => {
        setIsEditLoading(true);
        salesService
            .updateSaleProperty(sp.propertyId, values)
            .then(handleUpdated)
            .catch(() => messenger.showErrorMessage({ key: 'Error al actualizar la propiedad' }))
            .finally(() => setIsEditLoading(false));
    };

    const onRemovePressed = () => {
        salesService
            .removeFromSale(sp.propertyId)
            .then(onRemoved)
            .catch(() => messenger.showErrorMessage({ key: 'Error al quitar la propiedad de ventas' }));
    };

    const onUploadPhoto = (file: File) => {
        setIsUploadingPhoto(true);
        salesService
            .uploadPropertyPhoto(sp.propertyId, file)
            .then((photo) => setPhotos((prev) => [...prev, photo]))
            .catch(() => messenger.showErrorMessage({ key: 'Error al subir la foto' }))
            .finally(() => setIsUploadingPhoto(false));
    };

    const onDeletePhoto = (photoId: number) => {
        salesService
            .deletePropertyPhoto(sp.propertyId, photoId)
            .then(() => setPhotos((prev) => prev.filter((p) => p.id !== photoId)))
            .catch(() => messenger.showErrorMessage({ key: 'Error al eliminar la foto' }));
    };

    const updateListing = (updated: SaleListingDto) => {
        const updatedSp = { ...sp, listings: (sp.listings ?? []).map((l) => (l.id === updated.id ? updated : l)) };
        setSp(updatedSp);
        onListingUpdated(updatedSp);
    };

    const onPauseListing = () => {
        if (!activeListing) return;
        salesService
            .pauseListing(activeListing.id)
            .then(updateListing)
            .catch(() => messenger.showErrorMessage({ key: 'Error al pausar la publicación' }));
    };

    const onCloseListing = () => {
        if (!activeListing) return;
        salesService
            .closeListing(activeListing.id)
            .then(updateListing)
            .catch(() => messenger.showErrorMessage({ key: 'Error al cerrar la publicación' }));
    };

    const onSyncListing = () => {
        if (!activeListing) return;
        salesService
            .syncListing(activeListing.id)
            .then(updateListing)
            .catch(() => messenger.showErrorMessage({ key: 'Error al sincronizar la publicación' }));
    };

    return {
        sp,
        photos,
        activeListing,
        isExpanded,
        isUploadingPhoto,
        isEditLoading,
        isPublishModalVisible,
        isEditModalVisible,
        listingStatusColor: statusInfo?.color ?? '',
        listingStatusLabel: statusInfo?.label ?? '',
        propertyAddress,
        editForm,
        onToggleExpand,
        onPublishPressed,
        onPublishCanceled,
        onPublished,
        onEditPressed,
        onEditCanceled,
        onEditSubmit,
        onRemovePressed,
        onUploadPhoto,
        onDeletePhoto,
        onPauseListing,
        onCloseListing,
        onSyncListing,
    };
};
